import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AdminChrome } from '#bv/components/admin/AdminChrome'
import { OrderFulfillmentExtras, type FulfillmentCopyField } from '#bv/components/admin/OrderFulfillmentExtras'
import { deliveryFieldsFromCartLine } from '#bv/lib/adminOrderDeliveryFields'
import {
  adminConfirmCryptoPayment,
  adminDeleteOrder,
  adminRefundOrder,
  adminExportDisputePack,
  adminGetShopRevenueMonths,
  adminBackfillMmCheckoutSessions,
  adminGetDashboard,
  adminGetManualFfOrders,
  adminLoginWithGithub,
  adminLogout,
  adminUpdateBanner,
  adminUpdateFeatured,
  adminUpdateManualFfOrderStatus,
} from '#bv/lib/adminApi'
import type { AdminShopRevenueMonth } from '#bv/lib/adminApi'
import type { AdminCustomer, AdminOrder, ManualFfOrder, SiteBannerConfig } from '#bv/lib/adminTypes'
import { orderEligibleForDisputePack } from '#bv/lib/adminOrderDisputeEligibility'
import { manualFfOrderEligibleForDisputePack } from '#bv/lib/manualFfDisputeEligibility'
import type { FeatureIconName } from '#bv/components/FeatureIcons'
import { getSupabaseClient } from '#bv/lib/supabaseClient'
import { getManualFfStorageBackend } from '#bv/lib/manualFf'
import { isProductFamilyChildId, normalizeFeaturedProductIds } from '#bv/data/productFamilies'
import { productsShownInShop } from '#bv/data/products'
import { ProductsPanel } from '#bv/components/admin/ProductsPanel'
import { SmmOrdersPanel } from '#bv/components/admin/SmmOrdersPanel'

const ICON_OPTIONS: FeatureIconName[] = [
  'zap', 'lock', 'gamepad', 'gem', 'check', 'rocket', 'shield', 'package',
  'message', 'money', 'handshake', 'star', 'search', 'card', 'lockKey', 'alert',
]

const tabSchema = z.object({
  tab: z.enum(['orders', 'customers', 'ff', 'featured', 'products', 'smm', 'banner']).optional().default('orders'),
})

export const Route = createFileRoute('/admin/dashboard')({
  validateSearch: tabSchema,
  component: AdminDashboardPage,
})

/* ─── tiny inline SVGs ─── */

function IconOrders() {
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" /><rect x="9" y="3" width="6" height="4" rx="1" /></svg>
}
function IconPaid() {
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
}
function IconPending() {
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
}
function IconCancelled() {
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="15" x2="9" y1="9" y2="15" /><line x1="9" x2="15" y1="9" y2="15" /></svg>
}
function IconCustomers() {
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
}
function IconRevenue() {
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><line x1="12" x2="12" y1="2" y2="22" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg>
}
function IconRefresh() {
  return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" /></svg>
}
function IconSearch() {
  return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><line x1="21" x2="16.65" y1="21" y2="16.65" /></svg>
}
function IconCopy() {
  return <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="14" height="14" x="8" y="8" rx="2" /><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" /></svg>
}

/* ─── Mobile tab icons ─── */
function MobileTabIcon({ id }: { id: string }) {
  const s = { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const }
  switch (id) {
    case 'orders': return <svg {...s}><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" /><rect x="9" y="3" width="6" height="4" rx="1" /></svg>
    case 'customers': return <svg {...s}><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /></svg>
    case 'ff': return <svg {...s}><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" /></svg>
    case 'featured': return <svg {...s}><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" /></svg>
    case 'products': return <svg {...s}><path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" /><path d="m3.3 7 8.7 5 8.7-5" /><path d="M12 22V12" /></svg>
    case 'banner': return <svg {...s}><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" /><line x1="4" x2="4" y1="22" y2="15" /></svg>
    default: return null
  }
}

/* ─── Page ─── */

function AdminDashboardPage() {
  const { tab } = Route.useSearch()
  const navigate = useNavigate()
  const [token, setToken] = useState<string | null>(null)
  const [orders, setOrders] = useState<AdminOrder[]>([])
  const [customers, setCustomers] = useState<AdminCustomer[]>([])
  const [manualOrders, setManualOrders] = useState<ManualFfOrder[]>([])
  const [manualStorageBackend, setManualStorageBackend] = useState<'supabase' | 'file'>('file')
  const [banner, setBanner] = useState<SiteBannerConfig | null>(null)
  const [featuredProductIds, setFeaturedProductIds] = useState<number[]>([])
  const [saving, setSaving] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [lastLoadedAt, setLastLoadedAt] = useState<string | null>(null)
  const [shopRevenueMonths, setShopRevenueMonths] = useState<{
    thisMonth: AdminShopRevenueMonth
    lastMonth: AdminShopRevenueMonth
  } | null>(null)

  const load = useCallback(async (t: string) => {
    try {
      const d = await adminGetDashboard({ data: { token: t } })
      const ff = await adminGetManualFfOrders({ data: { token: t } })
      const ffBackend = await getManualFfStorageBackend()
      try {
        const rev = await adminGetShopRevenueMonths({ data: { token: t } })
        setShopRevenueMonths(rev)
      } catch {
        setShopRevenueMonths(null)
      }
      setOrders(d.orders)
      setCustomers(d.customers)
      setBanner(d.banner)
      setFeaturedProductIds(d.featuredProductIds ?? [])
      setManualOrders(ff.orders)
      setManualStorageBackend(ffBackend)
      setLastLoadedAt(new Date().toISOString())
    } catch {
      sessionStorage.removeItem('bv_admin_token')
      setToken(null)
      await navigate({ to: '/admin/login' })
    }
  }, [navigate])

  useEffect(() => {
    const boot = async () => {
      const existing = sessionStorage.getItem('bv_admin_token')
      if (existing) { setToken(existing); await load(existing); return }
      try {
        const supabase = getSupabaseClient()
        const { data } = await supabase.auth.getSession()
        const accessToken = data.session?.access_token
        const githubProviderToken = data.session?.provider_token
        if (!accessToken) { await navigate({ to: '/admin/login' }); return }
        const { token: exchanged } = await adminLoginWithGithub({ data: { accessToken, githubProviderToken } })
        sessionStorage.setItem('bv_admin_token', exchanged)
        setToken(exchanged)
        await load(exchanged)
      } catch { await navigate({ to: '/admin/login' }) }
    }
    void boot()
  }, [load, navigate])

  const logout = async () => {
    const t = sessionStorage.getItem('bv_admin_token')
    if (t) { try { await adminLogout({ data: { token: t } }) } catch { /* */ } }
    sessionStorage.removeItem('bv_admin_token')
    try {
      sessionStorage.removeItem('bv_admin_oauth_pending')
    } catch {
      /* */
    }
    try { const supabase = getSupabaseClient(); await supabase.auth.signOut() } catch { /* */ }
    await navigate({ to: '/admin/login' })
  }

  const saveBanner = async (next: SiteBannerConfig) => {
    const t = sessionStorage.getItem('bv_admin_token')
    if (!t) return
    setSaving(true)
    try {
      const r = await adminUpdateBanner({ data: { token: t, banner: { enabled: next.enabled, items: next.items.map((i) => ({ icon: i.icon, text: i.text })) } } })
      setBanner(r.banner)
    } finally { setSaving(false) }
  }

  if (!token || !banner) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4" style={{ background: '#050508' }}>
        <div className="relative h-10 w-10">
          <div className="absolute inset-0 animate-ping rounded-full bg-[#00bfff]/20" />
          <div className="absolute inset-0 animate-spin rounded-full border-2 border-transparent border-t-[#00bfff]" />
        </div>
        <p className="text-sm font-medium text-[#5a6d8a]">Loading dashboard…</p>
      </div>
    )
  }

  const activeTab = tab ?? 'orders'
  const paidOrders = orders.filter((o) => o.status === 'paid').length
  const pendingOrders = orders.filter((o) => o.status === 'pending').length
  const cancelledOrders = orders.filter((o) => o.status === 'cancelled').length
  const paidRevenue = orders.filter((o) => o.status === 'paid').reduce((sum, order) => sum + order.amountCents, 0)

  const statCards = [
    { label: 'Total Orders', value: orders.length, icon: <IconOrders />, tone: '#c4b5fd', bg: 'rgba(139,92,246,0.08)', border: 'rgba(139,92,246,0.2)' },
    { label: 'Paid', value: paidOrders, icon: <IconPaid />, tone: '#86efac', bg: 'rgba(34,197,94,0.08)', border: 'rgba(34,197,94,0.2)' },
    { label: 'Pending', value: pendingOrders, icon: <IconPending />, tone: '#fde68a', bg: 'rgba(250,204,21,0.08)', border: 'rgba(250,204,21,0.2)' },
    { label: 'Cancelled', value: cancelledOrders, icon: <IconCancelled />, tone: '#fca5a5', bg: 'rgba(248,113,113,0.08)', border: 'rgba(248,113,113,0.2)' },
    { label: 'Customers', value: customers.length, icon: <IconCustomers />, tone: '#93c5fd', bg: 'rgba(59,130,246,0.08)', border: 'rgba(59,130,246,0.2)' },
    { label: 'Card Revenue', value: `$${(paidRevenue / 100).toFixed(2)}`, icon: <IconRevenue />, tone: '#a7f3d0', bg: 'rgba(16,185,129,0.08)', border: 'rgba(16,185,129,0.2)' },
  ]

  return (
    <AdminChrome active={activeTab} title="Operations" subtitle="Orders, customers, catalog, and site announcement" onLogout={() => void logout()}>
      {/* Sync bar */}
      <div className="mb-5 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-white/[0.08] bg-white/[0.02] px-4 py-2.5">
        <div className="flex items-center gap-2 text-xs text-[#7a92b8]">
          <span className="inline-block h-2 w-2 rounded-full bg-emerald-400 shadow-sm shadow-emerald-400/40" />
          Last sync:{' '}
          <span className="font-semibold text-[#8ecfff]">
            {lastLoadedAt ? new Date(lastLoadedAt).toLocaleString() : '—'}
          </span>
        </div>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-1.5 text-xs font-semibold text-[#8ecfff] transition-all hover:border-[#00bfff]/30 hover:bg-[#00bfff]/10 hover:text-white disabled:opacity-50"
          disabled={refreshing}
          onClick={() => { if (!token) return; setRefreshing(true); void load(token).finally(() => setRefreshing(false)) }}
        >
          <span className={refreshing ? 'animate-spin' : ''}><IconRefresh /></span>
          {refreshing ? 'Syncing…' : 'Refresh'}
        </button>
      </div>

      {/* Stat grid */}
      <div className="mb-5 grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-6">
        {statCards.map((card) => (
          <div
            key={card.label}
            className="group relative overflow-hidden rounded-xl border px-3.5 py-3 transition-all duration-200 hover:scale-[1.02] hover:shadow-lg"
            style={{ borderColor: card.border, background: card.bg }}
          >
            <div className="absolute -right-2 -top-2 opacity-[0.06] transition-opacity group-hover:opacity-[0.12]">
              <div style={{ transform: 'scale(3)', color: card.tone }}>{card.icon}</div>
            </div>
            <div className="relative">
              <div className="mb-2" style={{ color: card.tone }}>{card.icon}</div>
              <p className="m-0 text-[0.62rem] font-bold uppercase tracking-[0.15em]" style={{ color: '#5a6d8a' }}>
                {card.label}
              </p>
              <p className="m-0 mt-1 text-xl font-extrabold tabular-nums" style={{ color: card.tone }}>
                {card.value}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Desktop tabs */}
      <div className="mb-5 hidden items-center gap-1 rounded-xl border border-white/[0.06] bg-white/[0.015] p-1 sm:flex">
        {(['orders', 'customers', 'ff', 'featured', 'products', 'smm', 'banner'] as const).map((t) => (
          <Link
            key={t}
            to="/admin/dashboard"
            search={{ tab: t }}
            className={`min-w-fit whitespace-nowrap rounded-lg px-3.5 py-2 text-xs font-semibold uppercase tracking-wider transition-all duration-200 ${
              activeTab === t ? 'text-white shadow-sm' : 'text-[#5a6d8a] hover:text-[#8ecfff]'
            }`}
            style={
              activeTab === t
                ? { background: 'linear-gradient(135deg, rgba(0, 191, 255,0.25), rgba(0, 191, 255,0.12))', boxShadow: 'inset 0 0 0 1px rgba(0, 191, 255,0.3)' }
                : undefined
            }
          >
            {t === 'ff' ? 'F&F' : t}
          </Link>
        ))}
      </div>

      {/* Panels */}
      <div className="pb-20 sm:pb-0">
        {activeTab === 'orders' ? (
          <OrdersPanel orders={orders} shopRevenueMonths={shopRevenueMonths} token={token} onRefresh={() => load(token)} />
        ) : activeTab === 'customers' ? (
          <CustomersPanel customers={customers} />
        ) : activeTab === 'ff' ? (
          <ManualFfPanel token={token} orders={manualOrders} storageBackend={manualStorageBackend} onUpdated={(next) => setManualOrders((prev) => prev.map((o) => (o.id === next.id ? next : o)))} />
        ) : activeTab === 'featured' ? (
          <FeaturedPanel token={token} productIds={featuredProductIds} onSaved={(ids) => setFeaturedProductIds(ids)} />
        ) : activeTab === 'products' ? (
          <ProductsPanel token={token} />
        ) : activeTab === 'smm' ? (
          <SmmOrdersPanel token={token} />
        ) : (
          <BannerPanel banner={banner} saving={saving} onSave={(b) => void saveBanner(b)} />
        )}
      </div>

      {/* Mobile bottom nav */}
      <nav className="fixed inset-x-0 bottom-0 z-40 border-t border-white/10 bg-[#0a080d]/95 p-1.5 backdrop-blur-lg sm:hidden" aria-label="Mobile dashboard tabs">
        <div className="mx-auto grid max-w-[720px] grid-cols-7 gap-0.5">
          {([
            ['orders', 'Orders'],
            ['customers', 'Users'],
            ['ff', 'F&F'],
            ['featured', 'Feat.'],
            ['products', 'SKU'],
            ['smm', 'SMM'],
            ['banner', 'Banner'],
          ] as const).map(([t, label]) => (
            <Link
              key={t}
              to="/admin/dashboard"
              search={{ tab: t }}
              className={`flex flex-col items-center gap-0.5 rounded-lg px-1 py-1.5 text-center transition-all ${
                activeTab === t ? 'text-white' : 'text-[#5a6d8a]'
              }`}
              style={
                activeTab === t
                  ? { background: 'rgba(0, 191, 255,0.18)', boxShadow: 'inset 0 0 0 1px rgba(0, 191, 255,0.25)' }
                  : undefined
              }
            >
              <MobileTabIcon id={t} />
              <span className="text-[0.55rem] font-bold uppercase tracking-wide">{label}</span>
            </Link>
          ))}
        </div>
      </nav>
    </AdminChrome>
  )
}

/* ─── Panel Section Card ─── */

function PanelCard({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <section
      className={`overflow-hidden rounded-2xl border shadow-2xl ${className}`}
      style={{
        borderColor: 'rgba(0,191,255,0.1)',
        background: 'linear-gradient(165deg, rgba(14,10,18,0.98), rgba(9,7,12,0.96))',
        boxShadow: '0 8px 40px rgba(0,0,0,0.4)',
      }}
    >
      {children}
    </section>
  )
}

function PanelHeader({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-white/[0.08] bg-black/30 px-5 py-3.5">
      {children}
    </div>
  )
}

/* ─── Channel Badge ─── */

function ChannelBadge({ channel }: { channel?: string }) {
  const c = channel ?? 'moneymotion'
  const map: Record<string, { label: string; bg: string; text: string; icon: React.ReactNode }> = {
    moneymotion: {
      label: 'Card',
      bg: 'rgba(59,130,246,0.12)',
      text: '#93c5fd',
      icon: <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="14" x="2" y="5" rx="2" /><line x1="2" x2="22" y1="10" y2="10" /></svg>,
    },
    paypal_ff: {
      label: 'PayPal',
      bg: 'rgba(59,130,246,0.15)',
      text: '#60a5fa',
      icon: <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" /></svg>,
    },
    crypto: {
      label: 'Crypto',
      bg: 'rgba(168,85,247,0.12)',
      text: '#c4b5fd',
      icon: <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /></svg>,
    },
  }
  const info = map[c] ?? map.moneymotion
  return (
    <span
      className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[0.6rem] font-bold uppercase tracking-wider"
      style={{ background: info.bg, color: info.text }}
    >
      {info.icon}
      {info.label}
    </span>
  )
}

/* ─── Status Pill ─── */

function StatusPill({ status }: { status: string }) {
  const map: Record<string, { bg: string; text: string; dot: string }> = {
    paid: { bg: 'rgba(34,197,94,0.12)', text: '#86efac', dot: '#22c55e' },
    pending: { bg: 'rgba(250,204,21,0.12)', text: '#fde68a', dot: '#eab308' },
    cancelled: { bg: 'rgba(248,113,113,0.12)', text: '#fca5a5', dot: '#ef4444' },
    refunded: { bg: 'rgba(168,85,247,0.12)', text: '#c4b5fd', dot: '#a855f7' },
  }
  const info = map[status] ?? map.pending
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[0.65rem] font-bold uppercase tracking-wide"
      style={{ background: info.bg, color: info.text }}
    >
      <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ background: info.dot, boxShadow: status === 'pending' ? `0 0 6px ${info.dot}` : undefined }} />
      {status}
    </span>
  )
}

/* ─── Featured ─── */

function slotsFromFeaturedIds(ids: number[]): number[] {
  const a = [...ids.slice(0, 4)]
  while (a.length < 4) a.push(0)
  return a
}

function FeaturedPanel({ token, productIds, onSaved }: { token: string; productIds: number[]; onSaved: (ids: number[]) => void }) {
  const shop = productsShownInShop()
  const [slots, setSlots] = useState<number[]>(() => slotsFromFeaturedIds(productIds))
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState('')

  useEffect(() => {
    setSlots(slotsFromFeaturedIds(normalizeFeaturedProductIds(productIds)))
  }, [productIds])

  const persist = async (ids: number[]) => {
    setBusy(true); setMsg('')
    try {
      const r = await adminUpdateFeatured({ data: { token, productIds: ids } })
      onSaved(r.featuredProductIds)
      setMsg(ids.length === 0 ? 'Cleared — homepage uses automatic picks.' : 'Saved.')
    } catch (e) { setMsg(e instanceof Error ? e.message : 'Save failed') }
    finally { setBusy(false) }
  }

  const tierSlotWarnings = slots
    .map((id, i) => (id > 0 && isProductFamilyChildId(id) ? i + 1 : 0))
    .filter((n) => n > 0)

  const onSave = () => {
    const seen = new Set<number>()
    const ordered: number[] = []
    for (const id of slots) {
      if (id > 0 && !seen.has(id)) {
        seen.add(id)
        ordered.push(id)
      }
    }
    void persist(ordered)
  }

  return (
    <PanelCard>
      <PanelHeader>
        <div>
          <h2 className="m-0 text-[0.95rem] font-extrabold tracking-tight">Homepage featured deals</h2>
          <p className="m-0 mt-1 text-[0.72rem] font-medium" style={{ color: 'rgba(168,196,232,0.55)' }}>
            Pick up to four hub cards (e.g. Netflix #200, Discord #214) — order = left to right. Hidden tier SKUs auto-map to
            their hub on save. Leave all empty for automatic bestseller picks.
          </p>
        </div>
      </PanelHeader>
      <div className="grid gap-3 p-5 sm:grid-cols-2">
        {[0, 1, 2, 3].map((i) => (
          <label key={i} className="block text-[0.78rem] font-semibold" style={{ color: '#8ecfff' }}>
            Slot {i + 1}
            <select
              className="bv-input mt-1.5 w-full rounded-lg px-3 py-2.5 text-sm font-normal text-[#f0f6ff]"
              value={slots[i] ?? 0}
              onChange={(e) => { const v = Number(e.target.value); setSlots((prev) => { const next = [...prev]; next[i] = Number.isFinite(v) && v > 0 ? v : 0; return next }) }}
            >
              <option value={0}>— Auto / empty —</option>
              {shop.map((p) => <option key={p.id} value={p.id}>{p.name} (#{p.id})</option>)}
            </select>
          </label>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2 border-t border-white/[0.06] px-5 py-3.5">
        <button type="button" disabled={busy} onClick={() => void onSave()} className="btn-primary rounded-xl px-5 py-2.5 text-sm disabled:opacity-50">
          {busy ? 'Saving…' : 'Save featured'}
        </button>
        <button type="button" disabled={busy} onClick={() => { setSlots([0, 0, 0, 0]); void persist([]) }} className="btn-chrome rounded-xl px-5 py-2.5 text-sm disabled:opacity-50">
          Clear (use auto)
        </button>
        {tierSlotWarnings.length > 0 ? (
          <p className="m-0 text-xs" style={{ color: '#fcd34d' }}>
            Slot {tierSlotWarnings.join(', ')} uses a hidden tier id — saving will remap to the hub card.
          </p>
        ) : null}
        {msg ? <p className="m-0 text-xs" style={{ color: /fail|error/i.test(msg) ? '#fca5a5' : '#86efac' }}>{msg}</p> : null}
      </div>
    </PanelCard>
  )
}

/* ─── Manual F&F ─── */

function ManualFfPanel({ token, orders, storageBackend, onUpdated }: { token: string; orders: ManualFfOrder[]; storageBackend: 'supabase' | 'file'; onUpdated: (order: ManualFfOrder) => void }) {
  const [busyOrderId, setBusyOrderId] = useState<string | null>(null)
  const [disputeBusyId, setDisputeBusyId] = useState<string | null>(null)
  const [disputeMessage, setDisputeMessage] = useState('')
  const [copiedFulfillmentKey, setCopiedFulfillmentKey] = useState<string | null>(null)
  const ffCopyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const copyFulfillment = (orderId: string, field: FulfillmentCopyField, text: string) => {
    const value = text.trim()
    if (!value) return
    void navigator.clipboard.writeText(value)
    setCopiedFulfillmentKey(`${orderId}:${field}`)
    if (ffCopyTimerRef.current) window.clearTimeout(ffCopyTimerRef.current)
    ffCopyTimerRef.current = window.setTimeout(() => setCopiedFulfillmentKey(null), 1500)
  }

  const updateStatus = async (orderId: string, status: 'verifying_payment' | 'paid' | 'delivered' | 'cancelled') => {
    setBusyOrderId(orderId)
    try { const { order } = await adminUpdateManualFfOrderStatus({ data: { token, orderId, status } }); onUpdated(order) }
    finally { setBusyOrderId(null) }
  }

  const refundOrder = async (order: ManualFfOrder) => {
    if (
      !window.confirm(
        `Mark this F&F order as refunded?\n\n${order.id}\n${order.buyerEmail}\n$${order.totalUsd.toFixed(2)}\n\nSend the refund in PayPal separately; this only updates admin records.`,
      )
    ) {
      return
    }
    setDisputeMessage('')
    setBusyOrderId(order.id)
    try {
      const res = await adminRefundOrder({ data: { token, orderId: order.id } })
      if (res.order) onUpdated(res.order)
    } catch (e) {
      setDisputeMessage(e instanceof Error ? e.message : 'Could not mark refunded')
    } finally {
      setBusyOrderId(null)
    }
  }

  return (
    <PanelCard>
      <PanelHeader>
        <div>
          <h2 className="m-0 text-[0.95rem] font-extrabold tracking-tight">Manual F&F Orders</h2>
          <p className="m-0 mt-1 max-w-xl text-[0.68rem] leading-snug" style={{ color: 'rgba(168,196,232,0.55)' }}>
            <strong className="text-[#8ecfff]/90">Dispute PDF</strong> — PayPal chargeback pack for{' '}
            <strong className="text-[#8ecfff]/90">paid</strong> or <strong className="text-[#8ecfff]/90">delivered</strong>{' '}
            rows.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="rounded-md px-2 py-0.5 text-[0.62rem] font-bold uppercase tracking-wider" style={{ background: storageBackend === 'supabase' ? 'rgba(34,197,94,0.14)' : 'rgba(250,204,21,0.14)', color: storageBackend === 'supabase' ? '#86efac' : '#fde047' }}>
            {storageBackend}
          </span>
          <p className="m-0 text-[0.72rem] font-medium tabular-nums" style={{ color: 'rgba(168,196,232,0.55)' }}>{orders.length} total</p>
        </div>
      </PanelHeader>
      {disputeMessage ? <p className="m-0 border-b border-white/[0.06] bg-black/15 px-5 py-2 text-xs text-red-300">{disputeMessage}</p> : null}
      <div className="space-y-3 p-5">
        {orders.length === 0 ? (
          <div className="py-8 text-center">
            <p className="m-0 text-sm font-semibold text-[#f0f6ff]/60">No manual F&F orders yet.</p>
          </div>
        ) : (
          orders.map((order) => (
            <div key={order.id} className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-4 transition-colors hover:border-white/[0.12]">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-mono text-xs text-[#8ecfff]/80">{order.id}</p>
                  <p className="text-sm font-semibold text-[#f0f6ff]">{order.buyerEmail}</p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-extrabold tabular-nums text-[#f0f6ff]">${order.totalUsd.toFixed(2)}</p>
                  <StatusPill status={order.status} />
                </div>
              </div>
              <div className="mt-2 text-xs text-[#5a6d8a]">
                Note ID: <span className="font-mono text-[#aef2ce]">{order.noteId}</span>
              </div>
              {order.items.length > 0 ? (
                <ul className="mt-3 space-y-2 border-t border-white/[0.06] pt-3 text-xs">
                  {order.items.map((item, idx) => (
                    <li key={`${order.id}-${idx}`} className="rounded-lg border border-white/[0.06] bg-black/20 px-3 py-2">
                      <p className="m-0 font-semibold text-[#f0f6ff]">
                        {item.name}
                        <span className="ml-1 font-normal text-[#5a6d8a]">×{item.quantity}</span>
                      </p>
                      <OrderFulfillmentExtras
                        order={{
                          id: order.id,
                          productId: item.productId,
                          productName: item.name,
                          amountCents: Math.round(item.unitPrice * item.quantity * 100),
                          currency: 'usd',
                          status: 'paid',
                          createdAt: order.createdAt,
                          ...deliveryFieldsFromCartLine(item),
                        }}
                        copiedKey={copiedFulfillmentKey}
                        onCopy={copyFulfillment}
                        compact
                      />
                    </li>
                  ))}
                </ul>
              ) : null}
              <div className="mt-3 flex flex-wrap gap-2">
                <button type="button" className="rounded-lg border border-violet-400/35 bg-violet-500/10 px-3 py-1.5 text-xs font-semibold text-violet-200 hover:bg-violet-500/20 disabled:cursor-not-allowed disabled:opacity-45" disabled={!manualFfOrderEligibleForDisputePack(order) || disputeBusyId === order.id || busyOrderId === order.id} onClick={() => { setDisputeMessage(''); setDisputeBusyId(order.id); void downloadDisputePack(token, order.id).catch((e) => setDisputeMessage(e instanceof Error ? e.message : 'Could not export PDF')).finally(() => setDisputeBusyId(null)) }}>
                  {disputeBusyId === order.id ? 'PDF…' : 'Dispute PDF'}
                </button>
                {order.status === 'paid' || order.status === 'delivered' ? (
                  <button type="button" className="rounded-lg border border-purple-400/35 bg-purple-500/10 px-3 py-1.5 text-xs font-semibold text-purple-200 hover:bg-purple-500/20 disabled:opacity-45" disabled={busyOrderId === order.id} onClick={() => void refundOrder(order)}>
                    {busyOrderId === order.id ? '…' : 'Refund'}
                  </button>
                ) : null}
                {order.status !== 'refunded' && order.status !== 'cancelled'
                  ? (['verifying_payment', 'paid', 'delivered', 'cancelled'] as const).map((s) => {
                      const labels: Record<string, string> = { verifying_payment: 'Verifying', paid: 'Mark Paid', delivered: 'Delivered', cancelled: 'Cancel' }
                      const cls = s === 'delivered' ? 'btn-primary' : s === 'cancelled' ? 'rounded-lg border border-red-400/30 text-red-300 hover:bg-red-500/10' : 'btn-chrome'
                      return (
                        <button key={s} type="button" className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${cls}`} disabled={busyOrderId === order.id} onClick={() => void updateStatus(order.id, s)}>
                          {labels[s]}
                        </button>
                      )
                    })
                  : null}
              </div>
            </div>
          ))
        )}
      </div>
    </PanelCard>
  )
}

/* ─── Revenue helpers ─── */

async function downloadDisputePack(adminToken: string, orderId: string): Promise<void> {
  const out = await adminExportDisputePack({ data: { token: adminToken, orderId } })
  const bin = atob(out.pdfBase64)
  const bytes = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
  const blob = new Blob([bytes], { type: 'application/pdf' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  const m = out.contentDisposition.match(/filename="([^"]+)"/)
  a.download = m?.[1] ?? `PulseDigital-dispute-pack-${orderId.replace(/[^a-zA-Z0-9_.-]+/g, '_').slice(0, 80)}.pdf`
  a.click()
  URL.revokeObjectURL(url)
}

function orderChannelLabel(o: AdminOrder): string {
  const c = o.channel ?? 'moneymotion'
  if (c === 'paypal_ff') return 'PayPal F&F'
  if (c === 'crypto') return 'Crypto'
  return 'Card'
}

/* ─── Revenue Bar ─── */

function RevenueBar({ label, total, mm, ff, crypto, pct, accent }: { label: string; total: number; mm: string; ff: string; crypto: string; pct: number; accent: 'green' | 'slate' }) {
  const isGreen = accent === 'green'
  return (
    <div
      className="rounded-xl border px-4 py-3.5"
      style={{
        borderColor: isGreen ? 'rgba(34,197,94,0.25)' : 'rgba(148,163,184,0.2)',
        background: isGreen ? 'rgba(34,197,94,0.06)' : 'rgba(148,163,184,0.04)',
      }}
    >
      <p className="m-0 text-[0.62rem] font-bold uppercase tracking-[0.15em]" style={{ color: isGreen ? '#86efac' : '#94a3b8' }}>
        {label}
      </p>
      <p className="m-0 mt-1.5 text-2xl font-extrabold tabular-nums" style={{ color: isGreen ? '#d1fae5' : '#e2e8f0' }}>
        ${total.toFixed(2)}
      </p>
      <div className="mt-2 flex gap-3 text-[0.62rem] font-medium tabular-nums" style={{ color: isGreen ? '#a7f3d0' : '#cbd5e1' }}>
        <span>MM {mm}</span>
        <span className="text-white/20">·</span>
        <span>PayPal {ff}</span>
        <span className="text-white/20">·</span>
        <span>Crypto {crypto}</span>
      </div>
      <div className="mt-2.5 h-1.5 overflow-hidden rounded-full bg-black/30">
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{
            width: `${pct}%`,
            background: isGreen ? 'linear-gradient(90deg, #22c55e, #4ade80)' : 'linear-gradient(90deg, #64748b, #94a3b8)',
          }}
        />
      </div>
    </div>
  )
}

/* ─── Orders Panel ─── */

const SERVER_BOOST_PRODUCT_IDS = new Set([134, 135])

function isServerBoostOrder(o: AdminOrder): boolean {
  return SERVER_BOOST_PRODUCT_IDS.has(o.productId) || /server boost/i.test(o.productName)
}

function cardOrderNeedsDeliverySync(o: AdminOrder): boolean {
  if ((o.channel ?? 'moneymotion') !== 'moneymotion') return false
  if (isServerBoostOrder(o)) return !(o.serverBoostInviteLink ?? '').trim()
  return false
}

function OrdersPanel({ orders, shopRevenueMonths, token, onRefresh }: {
  orders: AdminOrder[]
  shopRevenueMonths: { thisMonth: AdminShopRevenueMonth; lastMonth: AdminShopRevenueMonth } | null
  token: string
  onRefresh: () => Promise<void>
}) {
  const [query, setQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | AdminOrder['status']>('all')
  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'amount_desc'>('newest')
  const [disputeBusyId, setDisputeBusyId] = useState<string | null>(null)
  const [deleteBusyId, setDeleteBusyId] = useState<string | null>(null)
  const [confirmCryptoBusyId, setConfirmCryptoBusyId] = useState<string | null>(null)
  const [refundBusyId, setRefundBusyId] = useState<string | null>(null)
  const [disputeMessage, setDisputeMessage] = useState('')
  const [copiedFulfillmentKey, setCopiedFulfillmentKey] = useState<string | null>(null)
  const [syncDeliveryBusyId, setSyncDeliveryBusyId] = useState<string | null>(null)
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const PAGE_SIZE = 25
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)

  const syncCardDelivery = async (o: AdminOrder) => {
    const sid = (o.checkoutSessionId ?? o.id).trim()
    if (!sid) return
    setDisputeMessage('')
    setSyncDeliveryBusyId(o.id)
    try {
      await adminBackfillMmCheckoutSessions({ data: { token, checkoutSessionIds: [sid] } })
      await onRefresh()
    } catch (e) {
      setDisputeMessage(e instanceof Error ? e.message : 'Could not sync delivery from MoneyMotion')
    } finally {
      setSyncDeliveryBusyId(null)
    }
  }

  const copyFulfillment = (orderId: string, field: FulfillmentCopyField, text: string) => {
    const value = text.trim()
    if (!value) return
    void navigator.clipboard.writeText(value)
    setCopiedFulfillmentKey(`${orderId}:${field}`)
    if (copyTimerRef.current) window.clearTimeout(copyTimerRef.current)
    copyTimerRef.current = window.setTimeout(() => setCopiedFulfillmentKey(null), 1500)
  }

  const confirmCryptoPayment = (o: AdminOrder) => {
    if (!window.confirm(`Confirm payment for this crypto order?\n\n${o.id}\n${o.productName}\n\nThis will mark it as paid and update revenue.`)) return
    setDisputeMessage(''); setConfirmCryptoBusyId(o.id)
    void adminConfirmCryptoPayment({ data: { token, paymentId: o.id } })
      .then(() => onRefresh())
      .catch((e) => setDisputeMessage(e instanceof Error ? e.message : 'Could not confirm payment'))
      .finally(() => setConfirmCryptoBusyId(null))
  }

  const confirmDeleteOrder = (o: AdminOrder) => {
    if (!window.confirm(`Permanently delete this ${orderChannelLabel(o)} order?\n\n${o.id}\n${o.productName}\n\nThis cannot be undone.`)) return
    setDisputeMessage(''); setDeleteBusyId(o.id)
    void adminDeleteOrder({ data: { token, orderId: o.id } })
      .then(() => onRefresh())
      .catch((e) => setDisputeMessage(e instanceof Error ? e.message : 'Could not delete order'))
      .finally(() => setDeleteBusyId(null))
  }

  const confirmRefundOrder = (o: AdminOrder) => {
    if (
      !window.confirm(
        `Mark this ${orderChannelLabel(o)} order as refunded?\n\n${o.id}\n${o.productName}\n$${(o.amountCents / 100).toFixed(2)}\n\nThis updates admin records and monthly revenue. Process the actual refund in your payment provider separately.`,
      )
    ) {
      return
    }
    setDisputeMessage('')
    setRefundBusyId(o.id)
    void adminRefundOrder({ data: { token, orderId: o.id } })
      .then(() => onRefresh())
      .catch((e) => setDisputeMessage(e instanceof Error ? e.message : 'Could not mark refunded'))
      .finally(() => setRefundBusyId(null))
  }

  const statusCounts = useMemo(() => ({
    all: orders.length,
    pending: orders.filter((o) => o.status === 'pending').length,
    paid: orders.filter((o) => o.status === 'paid').length,
    cancelled: orders.filter((o) => o.status === 'cancelled').length,
    refunded: orders.filter((o) => o.status === 'refunded').length,
  }), [orders])

  const monthly = useMemo(() => {
    const cur = shopRevenueMonths?.thisMonth
    const prev = shopRevenueMonths?.lastMonth
    const totalThis = cur?.totalUsd ?? 0
    const totalLast = prev?.totalUsd ?? 0
    const labelThis = cur?.label ?? 'This month (UTC MTD)'
    const labelLast = prev?.label ?? 'Last month (UTC)'
    const max = Math.max(totalThis, totalLast, 0.01)
    let changeText = ''
    let changeTone: 'up' | 'down' | 'flat' | 'na' = 'na'
    if (totalLast > 0) {
      const pct = ((totalThis - totalLast) / totalLast) * 100
      changeText = `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}% vs last month`
      changeTone = pct > 0.5 ? 'up' : pct < -0.5 ? 'down' : 'flat'
    } else if (totalThis > 0) {
      changeText = 'New vs last month'
      changeTone = 'na'
    } else {
      changeText = 'No paid revenue'
      changeTone = 'na'
    }
    return {
      totalThis,
      totalLast,
      labelThis,
      labelLast,
      mmThisUsd: cur?.moneymotionUsd ?? 0,
      mmLastUsd: prev?.moneymotionUsd ?? 0,
      ffThisUsd: cur?.manualFfUsd ?? 0,
      ffLastUsd: prev?.manualFfUsd ?? 0,
      cThisUsd: cur?.cryptoUsd ?? 0,
      cLastUsd: prev?.cryptoUsd ?? 0,
      thisPct: Math.round((totalThis / max) * 100),
      lastPct: Math.round((totalLast / max) * 100),
      changeText,
      changeTone,
      countThis: cur?.rows ?? 0,
      countLast: prev?.rows ?? 0,
      revenueReady: !!shopRevenueMonths,
    }
  }, [shopRevenueMonths])

  const normalizedQuery = query.trim().toLowerCase()
  const filteredOrders = useMemo(() => {
    const matches = orders.filter((o) => {
      if (statusFilter !== 'all' && o.status !== statusFilter) return false
      if (!normalizedQuery) return true
      return o.id.toLowerCase().includes(normalizedQuery) || o.productName.toLowerCase().includes(normalizedQuery) || (o.customerEmail ?? '').toLowerCase().includes(normalizedQuery) || orderChannelLabel(o).toLowerCase().includes(normalizedQuery)
    })
    if (sortBy === 'oldest') return [...matches].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
    if (sortBy === 'amount_desc') return [...matches].sort((a, b) => b.amountCents - a.amountCents)
    return [...matches].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
  }, [normalizedQuery, orders, sortBy, statusFilter])

  const pagedOrders = filteredOrders.slice(0, visibleCount)
  const hasMore = visibleCount < filteredOrders.length

  useEffect(() => { setVisibleCount(PAGE_SIZE) }, [statusFilter, normalizedQuery, sortBy])

  return (
    <PanelCard>
      <PanelHeader>
        <div>
          <h2 className="m-0 text-[0.95rem] font-extrabold tracking-tight">Orders</h2>
          <p className="m-0 mt-1 max-w-xl text-[0.68rem] leading-snug" style={{ color: 'rgba(168,196,232,0.55)' }}>
            Card, PayPal F&F, and crypto orders combined. <strong className="text-[#8ecfff]/90">Dispute PDF</strong> for chargebacks.
          </p>
        </div>
        <p className="m-0 text-[0.72rem] font-semibold tabular-nums" style={{ color: 'rgba(168,196,232,0.55)' }}>
          {filteredOrders.length} shown / {orders.length} total
        </p>
      </PanelHeader>

      {/* Revenue comparison */}
      <div className="border-b border-white/[0.08] px-5 py-5" style={{ background: 'linear-gradient(135deg, rgba(34,197,94,0.04), rgba(0, 191, 255,0.03))' }}>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span style={{ color: '#86efac' }}><IconRevenue /></span>
            <p className="m-0 text-[0.68rem] font-extrabold uppercase tracking-[0.12em] text-[#5a6d8a]">Shop revenue (matches /internal/revenue)</p>
          </div>
          <span
            className="rounded-full px-2.5 py-1 text-[0.68rem] font-bold tabular-nums"
            style={{
              background: monthly.changeTone === 'up' ? 'rgba(34,197,94,0.12)' : monthly.changeTone === 'down' ? 'rgba(248,113,113,0.12)' : 'rgba(255,255,255,0.06)',
              color: monthly.changeTone === 'up' ? '#86efac' : monthly.changeTone === 'down' ? '#fca5a5' : '#5a6d8a',
            }}
          >
            {monthly.changeTone === 'up' ? '↑' : monthly.changeTone === 'down' ? '↓' : ''} {monthly.changeText}
          </span>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <RevenueBar
            label={monthly.labelThis}
            total={monthly.totalThis}
            mm={`$${monthly.mmThisUsd.toFixed(2)}`}
            ff={`$${monthly.ffThisUsd.toFixed(2)}`}
            crypto={`$${monthly.cThisUsd.toFixed(2)}`}
            pct={monthly.thisPct}
            accent="green"
          />
          <RevenueBar
            label={monthly.labelLast}
            total={monthly.totalLast}
            mm={`$${monthly.mmLastUsd.toFixed(2)}`}
            ff={`$${monthly.ffLastUsd.toFixed(2)}`}
            crypto={`$${monthly.cLastUsd.toFixed(2)}`}
            pct={monthly.lastPct}
            accent="slate"
          />
        </div>
      </div>

      {/* Filters */}
      <div className="space-y-3 border-b border-white/[0.08] bg-black/20 px-5 py-3.5">
        <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto]">
          <div className="relative">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#5a6d8a]"><IconSearch /></span>
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search order ID, product, or email…" className="bv-input w-full rounded-lg py-2.5 pl-8 pr-3 text-sm" />
          </div>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value as typeof sortBy)} className="bv-input rounded-lg px-3 py-2.5 text-sm sm:min-w-44">
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
            <option value="amount_desc">Highest amount</option>
          </select>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {([
            ['all', 'All', statusCounts.all],
            ['pending', 'Pending', statusCounts.pending],
            ['paid', 'Paid', statusCounts.paid],
            ['cancelled', 'Cancelled', statusCounts.cancelled],
            ['refunded', 'Refunded', statusCounts.refunded],
          ] as const).map(([value, label, count]) => (
            <button
              key={value} type="button"
              onClick={() => setStatusFilter(value)}
              className={`rounded-lg px-2.5 py-1.5 text-[0.68rem] font-bold uppercase tracking-wide transition-all ${statusFilter === value ? 'text-white shadow-sm' : 'text-[#7a92b8] hover:text-white'}`}
              style={statusFilter === value ? { background: 'rgba(0, 191, 255,0.2)', boxShadow: 'inset 0 0 0 1px rgba(0, 191, 255,0.3)' } : { background: 'rgba(255,255,255,0.03)' }}
            >
              {label} <span className="ml-0.5 opacity-60">({count})</span>
            </button>
          ))}
        </div>
      </div>

      {disputeMessage ? <p className="m-0 border-b border-white/[0.06] bg-red-500/5 px-5 py-2 text-xs text-red-300">{disputeMessage}</p> : null}

      {/* Order list */}
      <div className="overflow-x-auto">
        {orders.length === 0 ? (
          <div className="px-5 py-10">
            <div className="mx-auto max-w-2xl rounded-2xl border border-white/10 bg-black/30 p-6 text-center">
              <div className="mx-auto mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#00bfff]/10">
                <IconOrders />
              </div>
              <p className="m-0 text-base font-semibold text-[#f0f6ff]">No orders yet</p>
              <p className="mt-2 text-sm leading-relaxed" style={{ color: 'rgba(168,196,232,0.72)' }}>
                Orders from <strong className="text-[#d4e8ff]">card</strong>, <strong className="text-[#d4e8ff]">PayPal F&F</strong>, and <strong className="text-[#d4e8ff]">crypto</strong> will appear here once a payment is processed.
              </p>
            </div>
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="px-5 py-10 text-center">
            <p className="m-0 text-sm font-semibold text-[#f0f6ff]">No matching orders</p>
            <p className="mt-2 text-xs text-[#7a92b8]">Try clearing filters or adjusting your search.</p>
          </div>
        ) : (
          <>
            {/* Mobile cards */}
            <div className="space-y-2.5 p-4 sm:hidden">
              {pagedOrders.map((o) => (
                <article key={`${o.channel ?? 'moneymotion'}:${o.id}`} className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-3.5 transition-colors hover:border-white/[0.12]">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="mb-1"><ChannelBadge channel={o.channel} /></div>
                      <p className="truncate text-sm font-semibold text-[#f0f6ff]">{o.productName}</p>
                      <p className="mt-0.5 break-all font-mono text-[0.65rem] leading-snug text-[#8ecfff]/85">{o.id}</p>
                    </div>
                    <StatusPill status={o.status} />
                  </div>
                  <OrderFulfillmentExtras
                    order={o}
                    copiedKey={copiedFulfillmentKey}
                    onCopy={copyFulfillment}
                    compact
                  />
                  <p className="mt-2 text-xs text-[#8ecfff]/80">{o.customerEmail ?? '—'}</p>
                  <div className="mt-2 flex items-center justify-between gap-2 text-xs">
                    <p className="text-base font-extrabold tabular-nums text-[#d4e8ff]">${(o.amountCents / 100).toFixed(2)}</p>
                    <p className="text-right text-[0.65rem] text-[#5a6d8a]">{new Date(o.createdAt).toLocaleDateString()}</p>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {cardOrderNeedsDeliverySync(o) ? (
                      <button
                        type="button"
                        disabled={syncDeliveryBusyId === o.id}
                        className="rounded-lg border border-sky-400/35 bg-sky-500/10 px-2.5 py-1 text-[0.65rem] font-bold text-sky-200 hover:bg-sky-500/20 disabled:opacity-45"
                        onClick={() => void syncCardDelivery(o)}
                      >
                        {syncDeliveryBusyId === o.id ? '…' : 'Sync server link'}
                      </button>
                    ) : null}
                    {o.channel === 'crypto' && o.status === 'pending' ? (
                      <button type="button" disabled={confirmCryptoBusyId === o.id} className="rounded-lg border border-emerald-400/35 bg-emerald-500/10 px-2.5 py-1 text-[0.65rem] font-bold text-emerald-200 hover:bg-emerald-500/20 disabled:opacity-45" onClick={() => confirmCryptoPayment(o)}>
                        {confirmCryptoBusyId === o.id ? '…' : 'Confirm paid'}
                      </button>
                    ) : null}
                    {o.status === 'paid' ? (
                      <button type="button" disabled={refundBusyId === o.id} className="rounded-lg border border-purple-400/35 bg-purple-500/10 px-2.5 py-1 text-[0.65rem] font-bold text-purple-200 hover:bg-purple-500/20 disabled:opacity-45" onClick={() => confirmRefundOrder(o)}>
                        {refundBusyId === o.id ? '…' : 'Refund'}
                      </button>
                    ) : null}
                    <button type="button" disabled={!orderEligibleForDisputePack(o) || disputeBusyId === o.id} className="rounded-lg border border-violet-400/35 bg-violet-500/10 px-2.5 py-1 text-[0.65rem] font-bold text-violet-200 hover:bg-violet-500/20 disabled:opacity-45" onClick={() => { setDisputeMessage(''); setDisputeBusyId(o.id); void downloadDisputePack(token, o.id).catch((e) => setDisputeMessage(e instanceof Error ? e.message : 'PDF error')).finally(() => setDisputeBusyId(null)) }}>
                      {disputeBusyId === o.id ? '…' : 'PDF'}
                    </button>
                    <button type="button" disabled={deleteBusyId === o.id} className="rounded-lg border border-red-400/35 bg-red-500/10 px-2.5 py-1 text-[0.65rem] font-bold text-red-200 hover:bg-red-500/20 disabled:opacity-45" onClick={() => confirmDeleteOrder(o)}>
                      {deleteBusyId === o.id ? '…' : 'Del'}
                    </button>
                  </div>
                </article>
              ))}
            </div>

            {/* Desktop table */}
            <table className="orders-sheet hidden min-w-[80rem] w-full border-separate border-spacing-0 text-[0.78rem] sm:table">
              <thead>
                <tr style={{ background: '#0d0b10' }}>
                  {['Channel', 'ID', 'Product', 'Customer', 'Amount', 'Status', 'Date', 'Actions'].map((h) => (
                    <th key={h} className="whitespace-nowrap px-3 py-3 text-left text-[0.58rem] font-extrabold uppercase tracking-[0.15em] text-[#5a6d8a]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pagedOrders.map((o) => (
                  <tr key={`${o.channel ?? 'moneymotion'}:${o.id}`} className="border-b border-white/[0.05] transition-colors hover:bg-[#00bfff]/[0.04]">
                    <td className="px-3 py-2.5"><ChannelBadge channel={o.channel} /></td>
                    <td className="min-w-[14rem] max-w-[22rem] px-3 py-2.5 font-mono text-[0.65rem] leading-snug break-all text-[#8ecfff]/85">
                      {o.id}
                    </td>
                    <td className="px-3 py-2.5 font-medium text-[#f0f6ff]">
                      <div className="flex flex-col gap-1">
                        <span>{o.productName}</span>
                        <OrderFulfillmentExtras
                          order={o}
                          copiedKey={copiedFulfillmentKey}
                          onCopy={copyFulfillment}
                        />
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-[#8ecfff]/80">{o.customerEmail ?? '—'}</td>
                    <td className="px-3 py-2.5 font-semibold tabular-nums text-[#d4e8ff]">${(o.amountCents / 100).toFixed(2)}</td>
                    <td className="px-3 py-2.5"><StatusPill status={o.status} /></td>
                    <td className="px-3 py-2.5 text-[#5a6d8a]">{new Date(o.createdAt).toLocaleDateString()}</td>
                    <td className="px-3 py-2.5">
                      <div className="flex flex-wrap gap-1">
                        {cardOrderNeedsDeliverySync(o) ? (
                          <button
                            type="button"
                            disabled={syncDeliveryBusyId === o.id}
                            className="rounded-md border border-sky-400/35 bg-sky-500/10 px-2 py-0.5 text-[0.6rem] font-bold uppercase tracking-wide text-sky-200 hover:bg-sky-500/20 disabled:opacity-45"
                            onClick={() => void syncCardDelivery(o)}
                            title="Pull serverBoostInviteLink from MoneyMotion checkout metadata"
                          >
                            {syncDeliveryBusyId === o.id ? '…' : 'Sync link'}
                          </button>
                        ) : null}
                        {o.channel === 'crypto' && o.status === 'pending' ? (
                          <button type="button" disabled={confirmCryptoBusyId === o.id} className="rounded-md border border-emerald-400/35 bg-emerald-500/10 px-2 py-0.5 text-[0.6rem] font-bold uppercase tracking-wide text-emerald-200 hover:bg-emerald-500/20 disabled:opacity-45" onClick={() => confirmCryptoPayment(o)}>
                            {confirmCryptoBusyId === o.id ? '…' : 'Confirm'}
                          </button>
                        ) : null}
                        {o.status === 'paid' ? (
                          <button type="button" disabled={refundBusyId === o.id} className="rounded-md border border-purple-400/35 bg-purple-500/10 px-2 py-0.5 text-[0.6rem] font-bold uppercase tracking-wide text-purple-200 hover:bg-purple-500/20 disabled:opacity-45" onClick={() => confirmRefundOrder(o)}>
                            {refundBusyId === o.id ? '…' : 'Refund'}
                          </button>
                        ) : null}
                        <button type="button" disabled={!orderEligibleForDisputePack(o) || disputeBusyId === o.id} className="rounded-md border border-violet-400/35 bg-violet-500/10 px-2 py-0.5 text-[0.6rem] font-bold uppercase tracking-wide text-violet-200 hover:bg-violet-500/20 disabled:opacity-45" onClick={() => { setDisputeMessage(''); setDisputeBusyId(o.id); void downloadDisputePack(token, o.id).catch((e) => setDisputeMessage(e instanceof Error ? e.message : 'PDF error')).finally(() => setDisputeBusyId(null)) }}>
                          {disputeBusyId === o.id ? '…' : 'PDF'}
                        </button>
                        <button type="button" disabled={deleteBusyId === o.id} className="rounded-md border border-red-400/35 bg-red-500/10 px-2 py-0.5 text-[0.6rem] font-bold uppercase tracking-wide text-red-200 hover:bg-red-500/20 disabled:opacity-45" onClick={() => confirmDeleteOrder(o)}>
                          {deleteBusyId === o.id ? '…' : 'Del'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Load more */}
            {hasMore ? (
              <div className="border-t border-white/[0.06] px-5 py-3 text-center">
                <button type="button" onClick={() => setVisibleCount((c) => c + PAGE_SIZE)} className="rounded-lg border border-white/10 px-4 py-2 text-xs font-semibold text-[#8ecfff] transition-colors hover:bg-white/5">
                  Show more ({filteredOrders.length - visibleCount} remaining)
                </button>
              </div>
            ) : null}
          </>
        )}
      </div>
    </PanelCard>
  )
}

/* ─── Customers Panel ─── */

function CustomersPanel({ customers }: { customers: AdminCustomer[] }) {
  const [query, setQuery] = useState('')
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return customers
    return customers.filter((c) => c.email.toLowerCase().includes(q) || (c.name ?? '').toLowerCase().includes(q) || (c.source ?? '').toLowerCase().includes(q))
  }, [query, customers])

  const copyEmail = (email: string, id: string) => {
    void navigator.clipboard.writeText(email)
    setCopiedId(id)
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => setCopiedId(null), 1500)
  }

  return (
    <PanelCard>
      <PanelHeader>
        <div>
          <h2 className="m-0 text-[0.95rem] font-extrabold tracking-tight">Customers</h2>
          <p className="m-0 mt-1 text-[0.68rem]" style={{ color: 'rgba(168,196,232,0.55)' }}>
            All registered customer records. Click email to copy.
          </p>
        </div>
        <p className="m-0 text-[0.72rem] font-semibold tabular-nums" style={{ color: 'rgba(168,196,232,0.55)' }}>
          {filtered.length} / {customers.length}
        </p>
      </PanelHeader>

      {customers.length > 0 ? (
        <div className="border-b border-white/[0.08] bg-black/20 px-5 py-3">
          <div className="relative">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#5a6d8a]"><IconSearch /></span>
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search email, name, or source…" className="bv-input w-full rounded-lg py-2.5 pl-8 pr-3 text-sm" />
          </div>
        </div>
      ) : null}

      <div className="overflow-x-auto">
        {customers.length === 0 ? (
          <div className="px-5 py-10 text-center">
            <div className="mx-auto mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-blue-500/10 text-blue-300">
              <IconCustomers />
            </div>
            <p className="m-0 text-sm font-semibold text-[#f0f6ff]">No customers yet</p>
            <p className="mt-2 text-xs" style={{ color: 'rgba(168,196,232,0.55)' }}>
              Customer records will appear when orders come in.
            </p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="px-5 py-8 text-center">
            <p className="m-0 text-sm text-[#7a92b8]">No customers matching "{query}"</p>
          </div>
        ) : (
          <>
            {/* Mobile */}
            <div className="space-y-2.5 p-4 sm:hidden">
              {filtered.map((c) => (
                <article key={c.id} className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-3.5">
                  <div className="flex items-center justify-between gap-2">
                    <button type="button" onClick={() => copyEmail(c.email, c.id)} className="group flex items-center gap-1.5 text-left text-sm font-semibold text-[#f0f6ff] transition-colors hover:text-[#00bfff]" title="Copy email">
                      {c.email}
                      <span className="opacity-0 transition-opacity group-hover:opacity-100">{copiedId === c.id ? <span className="text-[0.6rem] text-emerald-300">Copied!</span> : <IconCopy />}</span>
                    </button>
                  </div>
                  <p className="mt-1 text-xs text-[#8ecfff]/80">{c.name ?? '—'}</p>
                  <div className="mt-2 flex items-center justify-between text-[0.65rem] text-[#5a6d8a]">
                    <span>{c.source ?? '—'}</span>
                    <span>{new Date(c.createdAt).toLocaleDateString()}</span>
                  </div>
                </article>
              ))}
            </div>

            {/* Desktop */}
            <table className="orders-sheet hidden w-full border-separate border-spacing-0 text-[0.78rem] sm:table">
              <thead>
                <tr style={{ background: '#0d0b10' }}>
                  {['Email', 'Name', 'Source', 'Since'].map((h) => (
                    <th key={h} className="px-3 py-3 text-left text-[0.58rem] font-extrabold uppercase tracking-[0.15em] text-[#5a6d8a]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr key={c.id} className="border-b border-white/[0.05] transition-colors hover:bg-[#00bfff]/[0.04]">
                    <td className="px-3 py-2.5">
                      <button type="button" onClick={() => copyEmail(c.email, c.id)} className="group inline-flex items-center gap-1.5 font-medium text-[#f0f6ff] transition-colors hover:text-[#00bfff]" title="Copy email">
                        {c.email}
                        <span className="opacity-0 transition-opacity group-hover:opacity-100">{copiedId === c.id ? <span className="text-[0.6rem] text-emerald-300">Copied</span> : <IconCopy />}</span>
                      </button>
                    </td>
                    <td className="px-3 py-2.5 text-[#8ecfff]/80">{c.name ?? '—'}</td>
                    <td className="px-3 py-2.5 text-[#5a6d8a]">{c.source ?? '—'}</td>
                    <td className="px-3 py-2.5 text-[#5a6d8a]">{new Date(c.createdAt).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </PanelCard>
  )
}

/* ─── Banner Panel ─── */

function BannerPanel({ banner, saving, onSave }: { banner: SiteBannerConfig; saving: boolean; onSave: (b: SiteBannerConfig) => void }) {
  const [enabled, setEnabled] = useState(banner.enabled)
  const [items, setItems] = useState(() => banner.items.map((i) => ({ ...i })))

  useEffect(() => { setEnabled(banner.enabled); setItems(banner.items.map((i) => ({ ...i }))) }, [banner])

  return (
    <PanelCard>
      <PanelHeader>
        <h2 className="m-0 text-[0.95rem] font-extrabold tracking-tight">Announcement bar</h2>
        <label className="flex cursor-pointer items-center gap-2 text-sm font-medium" style={{ color: '#8ecfff' }}>
          <div className="relative">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} className="peer sr-only" />
            <div className="h-5 w-9 rounded-full bg-white/10 transition-colors peer-checked:bg-[#00bfff]/40" />
            <div className="absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white/80 shadow transition-transform peer-checked:translate-x-4 peer-checked:bg-white" />
          </div>
          {enabled ? 'Live' : 'Off'}
        </label>
      </PanelHeader>

      <div className="space-y-3 p-5">
        <p className="text-xs leading-relaxed" style={{ color: '#5a6d8a' }}>
          Rotating strip in the storefront header. Changes apply after save.
        </p>
        {items.map((row, idx) => (
          <div key={idx} className="flex flex-col gap-2 rounded-xl border border-white/[0.08] bg-white/[0.02] p-3 sm:flex-row sm:items-center">
            <select value={row.icon} onChange={(e) => { const next = [...items]; next[idx] = { ...next[idx], icon: e.target.value as FeatureIconName }; setItems(next) }} className="bv-input rounded-lg px-2 py-2 text-sm sm:w-36">
              {ICON_OPTIONS.map((ic) => <option key={ic} value={ic}>{ic}</option>)}
            </select>
            <input value={row.text} onChange={(e) => { const next = [...items]; next[idx] = { ...next[idx], text: e.target.value }; setItems(next) }} className="bv-input min-w-0 flex-1 rounded-lg px-3 py-2 text-sm" />
            <button type="button" className="rounded-lg border border-red-400/30 px-3 py-2 text-xs font-semibold text-red-300 transition-colors hover:bg-red-500/10" onClick={() => setItems(items.filter((_, i) => i !== idx))}>
              Remove
            </button>
          </div>
        ))}
        <button type="button" className="w-full rounded-xl border border-dashed border-white/15 px-3 py-2.5 text-xs font-semibold text-[#8ecfff] transition-colors hover:border-[#00bfff]/30 hover:bg-[#00bfff]/5" onClick={() => setItems([...items, { icon: 'zap', text: 'New line' }])}>
          + Add line
        </button>
      </div>

      <div className="border-t border-white/[0.06] px-5 py-4">
        <button type="button" disabled={saving} onClick={() => onSave({ enabled, items })} className="btn-primary rounded-xl px-6 py-2.5 text-sm disabled:opacity-50">
          {saving ? 'Saving…' : 'Save announcement'}
        </button>
      </div>
    </PanelCard>
  )
}
